/**
 * Local-storage-backed state for kid device sign-in.
 *
 * Keys:
 *   svara.device_id         — one UUID per physical device, generated once
 *   svara.paired_students   — array of paired-student metadata
 *   svara.device_tokens     — object keyed by student_id, holding bearer tokens
 *   svara.active_student_id — currently signed-in kid on this device
 */

const KEY_DEVICE_ID = "svara.device_id";
const KEY_PAIRED = "svara.paired_students";
const KEY_TOKENS = "svara.device_tokens";
const KEY_ACTIVE = "svara.active_student_id";

export type PairedStudent = {
  id: string;
  display_name: string | null;
  avatar: string | null;
};

function readJson<T>(key: string, fallback: T): T {
  const raw = localStorage.getItem(key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson<T>(key: string, value: T): void {
  localStorage.setItem(key, JSON.stringify(value));
}

export function getOrCreateDeviceId(): string {
  const existing = localStorage.getItem(KEY_DEVICE_ID);
  if (existing) return existing;
  const id = crypto.randomUUID();
  localStorage.setItem(KEY_DEVICE_ID, id);
  return id;
}

export function listPairedStudents(): PairedStudent[] {
  return readJson<PairedStudent[]>(KEY_PAIRED, []);
}

export function addPairedStudent(student: PairedStudent, token: string): void {
  const list = listPairedStudents().filter((s) => s.id !== student.id);
  list.push(student);
  writeJson(KEY_PAIRED, list);
  const tokens = readJson<Record<string, string>>(KEY_TOKENS, {});
  tokens[student.id] = token;
  writeJson(KEY_TOKENS, tokens);
}

export function removePairedStudent(studentId: string): void {
  const list = listPairedStudents().filter((s) => s.id !== studentId);
  writeJson(KEY_PAIRED, list);
  const tokens = readJson<Record<string, string>>(KEY_TOKENS, {});
  delete tokens[studentId];
  writeJson(KEY_TOKENS, tokens);
  if (getActiveStudentId() === studentId) clearActiveStudentId();
}

export function getDeviceToken(studentId: string): string | null {
  const tokens = readJson<Record<string, string>>(KEY_TOKENS, {});
  return tokens[studentId] ?? null;
}

export function setActiveStudentId(id: string): void {
  localStorage.setItem(KEY_ACTIVE, id);
}

export function getActiveStudentId(): string | null {
  return localStorage.getItem(KEY_ACTIVE);
}

export function clearActiveStudentId(): void {
  localStorage.removeItem(KEY_ACTIVE);
}

export function hasActiveDeviceSession(): boolean {
  const id = getActiveStudentId();
  if (!id) return false;
  return getDeviceToken(id) !== null;
}
